import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode, type ReactPortal, useId } from 'react';
import { createPortal } from 'react-dom';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Button } from '../forms/Button';

interface ConfirmDialogProps {
	isOpen: boolean;
	title: string;
	message: string;
	confirmText?: string;
	cancelText?: string;
	onConfirm: () => void;
	onCancel: () => void;
	variant?: 'danger' | 'warning' | 'info';
	/** When true, the confirm button is hidden (e.g., informational-only dialogs) */
	hideConfirmButton?: boolean;
	/**
	 * When true, disables both buttons and prevents backdrop dismissal —
	 * indicates the confirm action is in flight.
	 */
	isProcessing?: boolean;
	/**
	 * Optional content under the message, for a confirmation that carries
	 * one small choice (e.g. which channels to send a message by).
	 */
	children?: ReactNode;
	/** When true, the confirm button is disabled (e.g. nothing chosen yet). */
	confirmDisabled?: boolean;
}

export const ConfirmDialog = ({
	isOpen,
	title,
	message,
	confirmText,
	cancelText,
	onConfirm,
	onCancel,
	variant = 'danger',
	hideConfirmButton = false,
	isProcessing = false,
	children,
	confirmDisabled = false,
}: ConfirmDialogProps): ReactPortal | null => {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const titleId = useId();

	if (!isOpen) {
		return null;
	}

	// The confirm button colour follows the dialog variant so this generic
	// dialog isn't always red: destructive ('danger') stays red, while
	// non-destructive confirmations ('info'/'warning') use the primary button.
	// `variant` defaults to 'danger', so existing delete dialogs are unchanged.
	const confirmButtonVariant = variant === 'danger' ? 'danger' : 'primary';
	const defaultConfirmText = variant === 'danger' ? t('Delete') : t('Confirm');

	// Portal to document.body and use z-[60] so this stacks above any Headless
	// UI Dialog that opened it. Without the portal, the dialog renders inside
	// the parent modal's portal/stacking context and z-index can't escape.
	return createPortal(
		<div className="fixed inset-0 z-[60] flex items-center justify-center">
			<div
				className={cn('absolute inset-0', colors.classes.backdropOverlay)}
				onClick={isProcessing ? undefined : onCancel}
			/>
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				className={cn(
					'relative rounded-lg shadow-2xl w-full max-w-md m-4',
					'border border-default',
					'ring-1 ring-black ring-opacity-5',
					colors.classes.bgPrimary,
				)}
			>
				<div className={cn('px-6 py-4 border-b', colors.classes.borderDefault)}>
					<div className="flex items-center gap-3">
						<FontAwesomeIcon
							icon={faExclamationTriangle}
							className={cn(
								'h-6 w-6',
								variant === 'danger' && 'text-danger',
								variant === 'warning' && 'text-warning',
								variant === 'info' && 'text-accent',
							)}
						/>
						<h2
							id={titleId}
							className={cn(
								'text-xl font-semibold',
								colors.classes.textPrimary,
							)}
						>
							{title}
						</h2>
					</div>
				</div>

				<div className="px-6 py-4">
					<p className={cn('text-sm', colors.classes.textSecondary)}>
						{message}
					</p>
					{children != null && <div className="mt-3">{children}</div>}
				</div>

				<div
					className={cn(
						'px-6 py-4 border-t flex justify-end gap-3',
						colors.classes.borderDefault,
					)}
				>
					<Button variant="outline" onClick={onCancel} disabled={isProcessing}>
						{cancelText ?? t('Cancel')}
					</Button>
					{!hideConfirmButton && (
						<Button
							variant={confirmButtonVariant}
							onClick={onConfirm}
							disabled={isProcessing || confirmDisabled}
						>
							{confirmText ?? defaultConfirmText}
						</Button>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
};
